/**
 * CalendarColorResolver
 *
 * Retreives & resolves the colors used for any given calendar(s).
 */

import CalendarColor from 'owa-service/lib/contract/CalendarColor';
import ColorScheme from 'owa-color-utils/lib/ColorScheme';
import LocalCacheForRemoteCalendarEntry from 'owa-service/lib/contract/LocalCacheForRemoteCalendarEntry';
import {
    DEPRECATED_ORANGE_CALENDAR_COLOR,
    ORANGE_CALENDAR_COLOR,
    generateColorScheme,
} from 'owa-color-utils/lib/colorUtils';
import {
    getCalendarEntry,
    getCalendarIdByFolderId,
    CalendarId,
    getCalendarEntryByFolderId,
} from 'owa-calendar-cache';
import { getCategoryColorsForCategory, getMasterCategoryList } from 'owa-categories';
import { trace } from 'owa-trace';
import {
    getColorSchemeForCalendarColor,
    registerCalendarColor,
    releaseCalendarColor,
    getDefaultColorSchemes,
} from './CalendarColorSchemes';
import { setCalendarColorScheme } from '../actions/internalActions';
import { getAssignedCalendarColor } from '../selectors/assignedCalendarColorSelectors';

/**
 * The properties that need to be present on an event in order to get it's color scheme
 */
export interface ColorableEvent {
    ParentFolderId?: {
        Id: string;
    };
    Categories?: string[];
}

/**
 * Gets calendar color scheme by folder id
 * @returns the color scheme for the calendar
 */
export function getCalendarColorSchemeByFolderId(calendarFolderId: string): ColorScheme {
    let calendarId = getCalendarIdByFolderId(calendarFolderId);

    if (calendarId) {
        return getCalendarColorScheme(calendarId);
    } else {
        trace.warn(
            '[getCalendarColorSchemeByFolderId] Failed to retrieve the calendar Id while choosing color scheme'
        );
        return getColorSchemeForCalendarColor(CalendarColor.LightBlue);
    }
}

/**
 * Gets calendar color scheme for the surface item model. Uses the category color if item is categorized, otherwise uses the
 * parent folderId to grab the calendar color.
 * @returns the color scheme for the item
 */
export function getCalendarEventColorScheme(model: ColorableEvent): ColorScheme {
    if (!model.ParentFolderId) {
        trace.warn('[getCalendarEventColorScheme] Calendar item does not have parent folder id');
        return getColorSchemeForCalendarColor(CalendarColor.LightBlue);
    }

    let categories = model && model.Categories;
    return getColorScheme(model.ParentFolderId.Id, categories);
}

/**
 * Gets calendar color scheme by calendar id
 * @param calendarId The calendar id to retrieve the color for
 * @param lockColor Optional param to prevent auto color resolution to pick that color
 * @returns The color scheme for the calendar
 */
export function getCalendarColorScheme(calendarId: CalendarId, lockColor?: boolean): ColorScheme {
    let assignedCalendarColors = getAssignedCalendarColor(calendarId.id);
    if (!assignedCalendarColors) {
        assignedCalendarColors = refreshAssignedColorScheme(calendarId, lockColor);
    }
    return assignedCalendarColors;
}

/** Refreshes the assigned color of a calendar based on the calendar color in the calendarsCache
 * @param calendarId id of calendar entry to refresh
 * @param lockColor Optional param to prevent auto color resolution to pick that color
 * @returns the color assigned to the calendar entry
 */
export function refreshAssignedColorScheme(
    calendarId: CalendarId,
    lockColor?: boolean
): ColorScheme {
    const calendarEntry = getCalendarEntry({ id: calendarId });

    if (getAssignedCalendarColor(calendarId.id)) {
        releaseCalendarColor(getAssignedCalendarColor(calendarId.id).calPrimary);
    }

    let assignedCalendarColors;
    if (
        calendarEntry &&
        calendarEntry.HexColorString &&
        calendarEntry.HexColorString != '' &&
        getDefaultColorSchemes().some(
            color => color.calPrimary.toLowerCase() == calendarEntry.HexColorString.toLowerCase()
        )
    ) {
        assignedCalendarColors = generateColorScheme(calendarEntry.HexColorString);
        registerCalendarColor(calendarEntry.HexColorString);
    } else if (
        calendarEntry &&
        calendarEntry.HexColorString &&
        calendarEntry.HexColorString != '' &&
        DEPRECATED_ORANGE_CALENDAR_COLOR.toLowerCase() == calendarEntry.HexColorString.toLowerCase()
    ) {
        // this shim is necessary to gracefully migrate users from deprecated orange to A11Y-compliant orange
        // since we don't want to fall back to the CalendarColor enum associated with deprecated orange
        // in backend storage (i.e. CalendarColor.Red = 2), which is a regression from user perspective
        assignedCalendarColors = generateColorScheme(ORANGE_CALENDAR_COLOR);
        registerCalendarColor(ORANGE_CALENDAR_COLOR);
    } else {
        assignedCalendarColors = getColorSchemeForCalendarColor(
            calendarEntry.CalendarColor,
            lockColor
        );
    }

    setCalendarColorScheme(calendarId.id, assignedCalendarColors);

    return assignedCalendarColors;
}

/**
 * Determines if the calendar color for a given calendar is resolved or not
 * @param calendarId id of calendar entry to look up
 */
export function isCalendarColorResolved(calendarId: CalendarId): boolean {
    return !!getAssignedCalendarColor(calendarId.id);
}

function getColorScheme(containerId: string, categories: string[]) {
    let colorScheme;

    // If there are any categories present, then the color scheme for the item is dependent on the categories for the item
    if (categories && categories.length > 0) {
        let categoryList;
        let calendarEntry = getCalendarEntryByFolderId(containerId);
        if (calendarEntry && (calendarEntry as LocalCacheForRemoteCalendarEntry).RemoteCategories) {
            categoryList = (calendarEntry as LocalCacheForRemoteCalendarEntry).RemoteCategories
                .MasterList;
        } else {
            // We need to get the master category list for the correct account
            const calendarId = getCalendarIdByFolderId(containerId);
            categoryList = getMasterCategoryList(calendarId ? calendarId.mailboxInfo : null);
        }

        let categoryColor = getCategoryColorsForCategory(categories[0], categoryList);
        colorScheme = generateColorScheme(categoryColor.iconColor);
    } else {
        // If the event doesn't have any categories, then the color scheme is assigned the calendar color
        colorScheme = getCalendarColorSchemeByFolderId(containerId);
    }

    return colorScheme;
}
